<template>
  <!-- Backdrop is rendered by App-level v-dialog; this file is the body. -->
  <div class="unlock-card">
    <!-- Phrase panel: the default state. Asks for the recovery phrase. -->
    <template v-if="panel === 'phrase'">
      <div class="card-h">
        <h3>Unlock your data</h3>
        <span class="h-sub">Encrypted account · needs your recovery phrase</span>
      </div>
      <div class="card-b">
        <p class="muted" style="font-size: 0.7813rem; margin-bottom: 12px">
          Enter your 24-word recovery phrase, or upload the recovery JSON file
          you saved when you turned double encryption on. Your phrase never
          leaves this device — we derive your private key locally to decrypt
          your transactions, then keep it on this device so you don't have
          to re-enter it on every reload.
        </p>

        <div class="row gap-8" style="margin-bottom: 10px; flex-wrap: wrap">
          <button class="btn ghost small" @click="loadFromFile">
            Upload recovery file
          </button>
          <input
            ref="fileInput"
            type="file"
            accept="application/json,.json"
            style="display: none"
            @change="onFilePicked"
          />
        </div>

        <!-- A real password field (single line) so password managers
             recognise and autofill it, and Enter submits. The reveal toggle
             lets the user eyeball the 24 words for typos. -->
        <form class="phrase-form" @submit.prevent="submit">
          <div class="phrase-input-wrap">
            <input
              v-model="phrase"
              :type="reveal ? 'text' : 'password'"
              class="phrase-input"
              name="recovery-phrase"
              autocomplete="current-password"
              spellcheck="false"
              autocapitalize="off"
              autocorrect="off"
              placeholder="Paste your 24-word recovery phrase"
            />
            <button
              type="button"
              class="phrase-reveal"
              :aria-label="reveal ? 'Hide recovery phrase' : 'Show recovery phrase'"
              :title="reveal ? 'Hide' : 'Show'"
              @click="reveal = !reveal"
            >
              <Icon :name="reveal ? 'eyeoff' : 'eye'" :size="16" />
            </button>
          </div>

          <div v-if="error" class="error-text">{{ error }}</div>

          <div class="row gap-8" style="margin-top: 14px">
            <button type="submit" class="btn primary" :disabled="!canSubmit || busy">
              {{ busy ? 'Unlocking…' : 'Unlock' }}
            </button>
            <button type="button" class="btn ghost" @click="panel = 'locked'">Cancel</button>
          </div>
        </form>
      </div>
    </template>

    <!-- Locked panel: shown when the user dismisses the phrase prompt.
         We do NOT just close the dialog — the underlying UI can't show
         encrypted data, so we explain the situation and give them a
         choice instead of silently dropping them into a blank app. -->
    <template v-else>
      <div class="card-h">
        <h3>
          <span class="lock-glyph" aria-hidden="true">🔒</span>
          Your data is encrypted
        </h3>
        <span class="h-sub">Locked on this device</span>
      </div>
      <div class="card-b">
        <p class="muted" style="font-size: 0.8125rem; margin-bottom: 16px">
          We can't show your transactions, balances, or accounts on this
          device without your 24-word recovery phrase. Enter it now to
          continue, or sign out if you'd rather come back later.
        </p>
        <div class="row gap-8" style="flex-wrap: wrap">
          <button class="btn primary" @click="panel = 'phrase'">
            Enter recovery phrase
          </button>
          <button class="btn ghost" @click="$emit('signout')">
            Sign out
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useEncryption } from '@/composables/useEncryption'
import Icon from '@/components/shell/Icon.vue'

const emit = defineEmits<{
  (e: 'unlocked'): void
  (e: 'signout'): void
}>()

const enc = useEncryption()
const phrase = ref('')
const reveal = ref(false)
const error = ref<string | null>(null)
const busy = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
// 'phrase' = the input form; 'locked' = the message shown after the user
// dismisses the form. We keep both inside this same dialog so the app
// never falls through to a half-broken locked view.
const panel = ref<'phrase' | 'locked'>('phrase')

const canSubmit = computed(() => {
  // BIP39 24-word mnemonic always has exactly 24 whitespace-separated tokens.
  // We keep the threshold loose so partial pastes still let the user click —
  // unlockWithMnemonic surfaces the precise validation error.
  return phrase.value.trim().split(/\s+/).filter(Boolean).length >= 12
})

function loadFromFile(): void {
  fileInput.value?.click()
}

async function onFilePicked(event: Event): Promise<void> {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  try {
    const text = await file.text()
    const json = JSON.parse(text)
    if (typeof json?.mnemonic !== 'string') {
      throw new Error('No mnemonic in this file.')
    }
    phrase.value = json.mnemonic
    error.value = null
  } catch (e: any) {
    error.value = 'Could not read that recovery file: ' + (e?.message ?? e)
  } finally {
    target.value = '' // allow re-picking the same file
  }
}

async function submit(): Promise<void> {
  if (!canSubmit.value || busy.value) return
  busy.value = true
  error.value = null
  try {
    await enc.unlock(phrase.value)
    emit('unlocked')
  } catch (e: any) {
    error.value = e?.message ?? 'Unlock failed'
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.unlock-card {
  background: var(--surface);
  border-radius: var(--radius-md, 8px);
  box-shadow: var(--elev-3, 0 8px 24px rgba(0, 0, 0, 0.2));
  overflow: hidden;
  min-width: 380px;
  max-width: 520px;
}
.phrase-form { margin: 0; }
.phrase-input-wrap { position: relative; display: flex; align-items: center; }
.phrase-input {
  width: 100%;
  font: inherit;
  font-family: var(--font-mono, monospace);
  font-size: 13px;
  padding: 11px 40px 11px 12px; /* room for the reveal button */
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--ink);
}
.phrase-input:focus {
  outline: none;
  border-color: var(--ink);
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--ink) 12%, transparent);
}
.phrase-reveal {
  position: absolute;
  right: 6px;
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--ink-3);
  cursor: pointer;
}
.phrase-reveal:hover { background: var(--surface-2); color: var(--ink); }
.error-text {
  color: var(--neg-ink);
  font-size: 12px;
  margin-top: 8px;
}
/* See EncryptionSettings.vue for the rationale on dimming --pos. Mirrored
   here so the unlock dialog's "Unlock" button matches the wizard's tone. */
.btn.primary {
  background: color-mix(in oklab, var(--pos) 62%, var(--surface-2));
  color: white;
  border: 1px solid color-mix(in oklab, var(--pos) 72%, var(--surface-2));
}
.btn.primary:hover {
  background: color-mix(in oklab, var(--pos) 70%, var(--surface-2));
}
.btn.small { font-size: 12px; padding: 4px 10px; }
.lock-glyph {
  margin-right: 6px;
  font-size: 0.9em;
}
</style>
